import React from "react";
import { IconButton } from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import OpenInFullIcon from "@mui/icons-material/OpenInFull";
import CloseFullscreenIcon from "@mui/icons-material/CloseFullscreen";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import PrimaryButton from "../../common/ui/PrimaryButton";
import useTheme from "../../common/hooks/useTheme";
import { normalizeMarkdownText } from "./markdown";
import classes from "./Chat.module.css";

type Props = {
  isVisible: boolean;
  isCollapsed: boolean;
  markdownText: string | null;
  onPost: () => void;
  onToggleCollapsed: () => void;
  onDismiss: () => void;
};

const ChatPlainPostPanel = ({
  isVisible,
  isCollapsed,
  markdownText,
  onPost,
  onToggleCollapsed,
  onDismiss,
}: Props) => {
  const theme = useTheme();

  if (!isVisible) {
    return null;
  }

  return (
    <aside
      className={`${classes.plainPostPanel} ${
        isCollapsed ? classes.plainPostPanelCollapsed : ""
      }`}
      aria-label="Plain response panel"
    >
      <div className={classes.plainPostPanelHeader}>
        <h3 className={classes.plainPostPanelTitle}>
          Prescription Suggestion Panel
        </h3>
        <div className={classes.plainPostPanelHeaderActions}>
          <IconButton
            size="small"
            aria-label={
              isCollapsed
                ? "Expand prescription suggestion panel"
                : "Collapse prescription suggestion panel"
            }
            onClick={onToggleCollapsed}
            className={classes.plainPostPanelDismissButton}
            sx={{
              border: `1px solid ${theme.colors_ds.NEUTRAL_BORDER_WEAK}`,
              color: theme.colors_ds.NEUTRAL_ICON_STRONG,
              backgroundColor: theme.colors_ds.NEUTRAL_BG_MAIN,
              borderRadius: "8px",
              "&:hover": {
                backgroundColor: theme.colors_ds.NEUTRAL_BG_ELEVATION1,
              },
            }}
          >
            {isCollapsed ? (
              <OpenInFullIcon fontSize="small" />
            ) : (
              <CloseFullscreenIcon fontSize="small" />
            )}
          </IconButton>
          <IconButton
            size="small"
            aria-label="Dismiss prescription suggestion panel"
            onClick={onDismiss}
            className={classes.plainPostPanelDismissButton}
            sx={{
              border: `1px solid ${theme.colors_ds.NEUTRAL_BORDER_WEAK}`,
              color: theme.colors_ds.NEUTRAL_ICON_STRONG,
              backgroundColor: theme.colors_ds.NEUTRAL_BG_MAIN,
              borderRadius: "8px",
              "&:hover": {
                backgroundColor: theme.colors_ds.NEUTRAL_BG_ELEVATION1,
              },
            }}
          >
            <CloseIcon fontSize="small" />
          </IconButton>
        </div>
      </div>
      {!isCollapsed && (
        <>
          <div className={classes.plainPostPanelContent}>
            <div
              className={`${classes.messageBubble} ${classes.assistantBubble} ${classes.plainPostBubble}`}
            >
              <div
                className={`${classes.messageText} ${classes.messageMarkdown} ${classes.plainPostMarkdownDisplay}`}
              >
                <ReactMarkdown remarkPlugins={[remarkGfm]}>
                  {normalizeMarkdownText(markdownText || "")}
                </ReactMarkdown>
              </div>
            </div>
          </div>
          <div className={classes.plainPostPanelFooter}>
            <PrimaryButton
              label="POST"
              onClick={onPost}
              sx={{
                minWidth: "124px",
                justifyContent: "center",
              }}
              customLabelStyles={{
                textAlign: "center",
                width: "100%",
              }}
            />
          </div>
        </>
      )}
    </aside>
  );
};

export default ChatPlainPostPanel;
